import type { ComponentTemplate } from "@editor/types/component-template";
import type { MenuItem } from "@replo/design-system/components/menu/Menu";

import * as React from "react";

import ComponentTemplateDragOverlay from "@editor/components/ComponentTemplateDragOverlay";
import IsolatedReploComponent from "@editor/components/IsolatedReploComponent";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { useModal } from "@editor/hooks/useModal";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { getOptimizedImageUrl } from "@editor/utils/component-template";
import { getDashboardUrl } from "@editor/utils/env";

import { successToast } from "@replo/design-system/components/alert/Toast";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Menu } from "@replo/design-system/components/menu/Menu";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";
import { ImageOff, Link, MoreHorizontal, Trash } from "lucide-react";

const ComponentPreview = ({
  componentTemplate,
  imageError,
  setImageError,
}: {
  componentTemplate: ComponentTemplate;
  imageError: boolean;
  setImageError: (error: boolean) => void;
}) => {
  const [imageLoading, setImageLoading] = React.useState(true);

  if (isFeatureEnabled("component-previews") && componentTemplate.template) {
    return (
      <IsolatedReploComponent
        component={componentTemplate.template}
        variant="desktop"
      />
    );
  }

  if (imageError) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center p-4">
        <ImageOff size={24} className="text-muted mb-2" />
        <p className="text-muted text-center text-xs">No preview available</p>
      </div>
    );
  }

  // Get optimized image URL for preview
  const optimizedImageUrl = getOptimizedImageUrl(
    componentTemplate.desktopPreviewUrl,
    {
      width: 800,
      fit: "contain",
    },
  );

  return (
    <>
      {imageLoading && (
        <div className="absolute inset-0 h-full w-full">
          <Skeleton className="h-full w-full rounded-lg" />
        </div>
      )}
      <img
        src={optimizedImageUrl}
        loading="lazy"
        className={twMerge(
          "max-h-full max-w-full rounded-lg object-contain transition duration-700 ease-in-out",
          imageLoading ? "opacity-0" : "opacity-100",
        )}
        alt={`${componentTemplate.name} preview`}
        onError={() => setImageError(true)}
        onLoad={() => setImageLoading(false)}
      />
    </>
  );
};

export const SavedTemplateListItem = ({
  componentTemplate,
}: {
  componentTemplate: ComponentTemplate;
}) => {
  const modal = useModal();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const logEvent = useLogAnalytics();
  const [imageError, setImageError] = React.useState(false);

  const onCopyUrl = () => {
    const url = getDashboardUrl();
    const baseUrl = `${url}/template/${componentTemplate.id}`;
    void navigator.clipboard.writeText(baseUrl);
    logEvent("editor.componentTemplate.share", {
      shareUrl: baseUrl,
      componentTemplateId: componentTemplate?.id,
      componentTemplateName: componentTemplate?.name,
      from: "componentTemplateDetails",
    });
    successToast(
      "Template Link Copied",
      "The template link has been copied to your clipboard.",
    );
  };

  const menuItems: MenuItem[] = [
    {
      id: "shareTemplate",
      type: "leaf" as const,
      title: "Share",
      variant: "default" as const,
      size: "sm" as const,
      startEnhancer: <Link size={12} />,
      onSelect: () => onCopyUrl(),
    },

    {
      id: "deleteTemplate",
      type: "leaf" as const,
      title: "Delete",
      variant: "default" as const,
      size: "sm" as const,
      startEnhancer: <Trash size={12} />,
      onSelect: () => {
        modal.openModal({
          type: "deleteComponentTemplateConfirmationModal",
          props: {
            template: componentTemplate,
          },
        });
      },
    },
  ];

  return (
    <li
      key={componentTemplate.id}
      className="border-0.5 border-border group flex flex-col justify-between gap-1 rounded-lg p-1.5"
    >
      {/* Image Preview */}
      <div className="bg-sheet relative w-full cursor-pointer rounded">
        <div
          className="border-0.5 border-border flex h-full w-full place-items-center rounded-sm"
          data-testid="component-template-draggable"
        >
          <div className="flex aspect-[16/9] h-full w-full items-center justify-center p-2">
            <ComponentPreview
              componentTemplate={componentTemplate}
              imageError={imageError}
              setImageError={setImageError}
            />
            <ComponentTemplateDragOverlay
              dragType="section"
              template={componentTemplate}
              identifier={componentTemplate.id}
              scope="left-bar"
            />
          </div>
        </div>
      </div>

      {/* Name and Menu */}
      <div className="relative flex min-h-6 items-center justify-between gap-1">
        <span className="block truncate text-sm font-medium">
          {componentTemplate.name}
        </span>
        <Menu
          isOpen={isMenuOpen}
          onRequestOpen={() => setIsMenuOpen(true)}
          onRequestClose={() => setIsMenuOpen(false)}
          align="start"
          size="sm"
          side="right"
          sideOffset={4}
          disableTriggerFocusOnClose
          items={menuItems}
          contentClassNames="w-40"
          trigger={
            <IconButton
              variant="tertiary"
              size="sm"
              layoutClassName="h-4 w-4"
              icon={<MoreHorizontal size={12} />}
              aria-hidden
            />
          }
        />
      </div>
    </li>
  );
};
